import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CampaignDAGViewer,
  Cluster,
  EmptyState,
  Field,
  Grid,
  Input,
  Modal,
  Page,
  QueryState,
  Select,
  Stack,
  TextPostPreview,
  Timeline,
  type DagViewNode,
  type TimelineEntry,
  type Tone,
} from '@integra/ui';
import {
  useAsset,
  useAssetPreview,
  useCampaign,
  useCampaignExecutions,
  useDecideApproval,
  useExecuteCampaign,
  useExecutionDetail,
  useScheduleCampaign,
} from '../api/hooks.js';
import type { CampaignExecution, DagNode, ExecutionSummary, NodeExecution } from '../api/types.js';

/**
 * Campaign execution monitor and timeline (§20.4).
 *
 * Data access is exclusively through hooks (§21.4); this component issues no
 * requests of its own.
 */

const NODE_STATE_TONE: Record<string, Tone> = {
  pending: 'neutral',
  queued: 'info',
  running: 'info',
  completed: 'success',
  failed: 'error',
  rolled_back: 'warning',
  skipped: 'warning',
};

const RUN_STATE_TONE: Record<string, Tone> = {
  running: 'info',
  completed: 'success',
  failed: 'error',
  rejected: 'warning',
  cancelled: 'warning',
};

const formatTime = (value: string | null): string =>
  value === null ? '—' : new Date(value).toLocaleString();

/**
 * Report a run's outcome without flattering it. A campaign that failed AND
 * rolled work back has two facts the operator needs, not one (§8.3): what broke,
 * and what was undone as a consequence.
 */
/**
 * The assets a review gate is guarding: every `publish` node downstream of the
 * gate (transitively) names an asset, and those are exactly what will go out if
 * the reviewer approves. So the reviewer sees WHAT they are signing off, not just
 * a gate id — review means seeing, for every workspace (§8.2, §20.3).
 */
function assetsGuardedByGate(gateNodeId: string, nodes: readonly DagNode[]): string[] {
  const reachable = new Set<string>();
  let frontier = [gateNodeId];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const n of nodes) {
      if (!reachable.has(n.nodeId) && n.dependsOn.some((d) => frontier.includes(d))) {
        reachable.add(n.nodeId);
        next.push(n.nodeId);
      }
    }
    frontier = next;
  }
  const assetIds: string[] = [];
  for (const n of nodes) {
    if (reachable.has(n.nodeId) && n.action === 'publish') {
      const a = n.inputs['assetId'];
      if (typeof a === 'string' && a.length > 0 && !assetIds.includes(a)) assetIds.push(a);
    }
  }
  return assetIds;
}

function assetString(data: Record<string, unknown>, key: string): string | null {
  const v = data[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** One "what you're approving" card — the asset rendered as it will publish. */
function ReviewAssetCard({ workspaceId, assetId }: { workspaceId: string; assetId: string }) {
  const asset = useAsset(workspaceId, assetId);
  const data = (asset.data?.data ?? {}) as Record<string, unknown>;
  const hasStoredMedia = assetString(data, 'mediaPath') !== null;
  const preview = useAssetPreview(workspaceId, hasStoredMedia ? assetId : '');
  const mediaUrl = preview.url ?? assetString(data, 'imageUrl');
  const title = assetString(data, 'title') ?? assetString(data, 'name');
  const body =
    assetString(data, 'body') ??
    assetString(data, 'text') ??
    assetString(data, 'caption') ??
    assetString(data, 'description');
  const type = asset.data?.type ?? 'asset';

  return (
    <article className="overflow-hidden rounded-md border border-edge bg-surface">
      <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-surface-sunken">
        {mediaUrl !== null && mediaUrl !== undefined ? (
          <img src={mediaUrl} alt={title ?? 'preview'} className="h-full w-full object-contain" />
        ) : (
          <TextPostPreview title={title} body={body} kicker={type} />
        )}
      </div>
      <div className="px-3 py-2">
        {title !== null && <p className="truncate text-sm font-semibold text-navy-700">{title}</p>}
        {body !== null && (
          <p className="mt-0.5 line-clamp-3 whitespace-pre-line text-xs text-ink-muted">{body}</p>
        )}
      </div>
    </article>
  );
}

/** The content a gate guards, shown inline so review means seeing, not guessing. */
function GateReviewPreview({ workspaceId, assetIds }: { workspaceId: string; assetIds: string[] }) {
  if (assetIds.length === 0) return null;
  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
        What you&rsquo;re approving
      </p>
      <Grid min="sm" gap="sm">
        {assetIds.map((id) => (
          <ReviewAssetCard key={id} workspaceId={workspaceId} assetId={id} />
        ))}
      </Grid>
    </div>
  );
}

function OutcomeReport({ summary }: { summary: ExecutionSummary }) {
  if (summary.outcome === 'awaiting_approval') {
    const gates = summary.pendingApprovals;
    return (
      <Alert tone="warning" title="Paused at an approval gate">
        {gates.length === 0
          ? 'The run is waiting on a human decision.'
          : `The run is paused and will not continue until ${gates.join(', ')} ${
              gates.length === 1 ? 'is' : 'are'
            } decided.`}
      </Alert>
    );
  }

  if (summary.outcome === 'completed') {
    return (
      <Alert tone="success" title="Run completed">
        Every node reached a completed state.
      </Alert>
    );
  }

  return (
    <Alert
      tone={summary.outcome === 'rejected' ? 'warning' : 'error'}
      title={summary.outcome === 'rejected' ? 'Run rejected' : 'Run failed'}
    >
      <Stack gap="sm">
        {summary.failures.length > 0 && (
          <div>
            <p className="font-semibold">Failures</p>
            <ul className="list-disc pl-4">
              {summary.failures.map((failure) => (
                <li key={failure.nodeId}>
                  <span className="font-mono text-xs">{failure.nodeId}</span>: {failure.error}
                </li>
              ))}
            </ul>
          </div>
        )}
        {summary.rolledBack.length > 0 && (
          <div>
            <p className="font-semibold">Rolled back</p>
            <p>
              Completed work was undone in reverse order:{' '}
              <span className="font-mono text-xs">{summary.rolledBack.join(', ')}</span>
            </p>
          </div>
        )}
        {summary.failures.length === 0 && summary.rolledBack.length === 0 && (
          <p>The engine reported no per-node failures and nothing was rolled back.</p>
        )}
      </Stack>
    </Alert>
  );
}

export function CampaignDetailPage() {
  const { workspaceId = '', campaignId = '' } = useParams<{
    workspaceId: string;
    campaignId: string;
  }>();

  const campaignQuery = useCampaign(workspaceId, campaignId);
  const executionsQuery = useCampaignExecutions(workspaceId, campaignId);
  const executeCampaign = useExecuteCampaign(workspaceId, campaignId);
  const decideApproval = useDecideApproval(workspaceId, campaignId);
  const scheduleCampaign = useScheduleCampaign(workspaceId, campaignId);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');

  const [chosenExecutionId, setChosenExecutionId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Newest first, so "the most recent run" is a defined thing rather than
  // whatever order the API happened to return.
  const executions: CampaignExecution[] = [...(executionsQuery.data?.data ?? [])].sort((a, b) =>
    a.startedAt < b.startedAt ? 1 : -1,
  );
  const defaultExecution = executions[0];
  const activeExecutionId =
    chosenExecutionId ?? (defaultExecution === undefined ? '' : defaultExecution.id);
  const activeExecution = executions.find((execution) => execution.id === activeExecutionId);

  const detailQuery = useExecutionDetail(workspaceId, campaignId, activeExecutionId);
  const nodeExecutions: NodeExecution[] = detailQuery.data?.data ?? [];
  const stateByNode = new Map(nodeExecutions.map((node) => [node.nodeKey, node]));

  const campaign = campaignQuery.data;
  const dagNodes = campaign?.dag.nodes ?? [];

  // The definition merged with the selected run's live state — one picture, not
  // a static graph beside a separate status list.
  const viewNodes: DagViewNode[] = dagNodes.map((node) => {
    const execution = stateByNode.get(node.nodeId);
    return {
      nodeId: node.nodeId,
      action: node.action,
      // Root nodes (ingest) omit dependsOn in the DAG JSON, so it arrives
      // undefined; the view type requires an array. Normalise here rather than
      // let an undefined reach the viewer's `.length`.
      dependsOn: node.dependsOn ?? [],
      ...(execution === undefined ? {} : { state: execution.state }),
    };
  });

  const selectedNode =
    selectedNodeId === null ? undefined : dagNodes.find((node) => node.nodeId === selectedNodeId);
  const selectedExecution = selectedNodeId === null ? undefined : stateByNode.get(selectedNodeId);

  /**
   * Gates currently waiting on a human (§8.2): an `approve` node the engine has
   * moved to `running` is not doing work — it is holding the campaign open.
   */
  const openGates = dagNodes.filter(
    (node) => node.action === 'approve' && stateByNode.get(node.nodeId)?.state === 'running',
  );

  // Node executions carry no timestamps, so the definition order is the only
  // stable ordering available; it is also the order the graph was authored in.
  const timelineEntries: TimelineEntry[] = dagNodes
    .map((node): TimelineEntry | null => {
      const execution = stateByNode.get(node.nodeId);
      if (execution === undefined) return null;
      const detail =
        execution.error !== null && execution.error !== ''
          ? execution.error
          : `${node.action} · attempt ${String(execution.attempt)}`;
      const tone = NODE_STATE_TONE[execution.state];
      return {
        id: node.nodeId,
        title: `${node.nodeId}: ${execution.state.replace('_', ' ')}`,
        detail,
        ...(tone === undefined ? {} : { tone }),
      };
    })
    .filter((entry): entry is TimelineEntry => entry !== null);

  const latestSummary: ExecutionSummary | undefined = decideApproval.data ?? executeCampaign.data;

  return (
    <Page
      title={campaign?.name ?? 'Campaign'}
      description="The definition, its run history, and the live state of every node in the selected run."
      actions={
        <Cluster gap="sm">
          <Button
            variant="secondary"
            onClick={() => {
              setScheduleAt(localInputValue(new Date(Date.now() + 60 * 60 * 1000)));
              setScheduleOpen(true);
            }}
          >
            {campaign?.schedule != null && campaign.schedule.state === 'scheduled'
              ? 'Reschedule'
              : 'Schedule'}
          </Button>
          <Button onClick={() => executeCampaign.mutate()} loading={executeCampaign.isPending}>
            Execute now
          </Button>
        </Cluster>
      }
    >
      <Stack gap="lg">
        {executeCampaign.error !== null && (
          <Alert tone="error" title="Could not start the run">
            {executeCampaign.error instanceof Error
              ? executeCampaign.error.message
              : 'Unknown error'}
          </Alert>
        )}
        {decideApproval.error !== null && (
          <Alert tone="error" title="Could not record the decision">
            {decideApproval.error instanceof Error ? decideApproval.error.message : 'Unknown error'}
          </Alert>
        )}
        {latestSummary !== undefined && <OutcomeReport summary={latestSummary} />}

        <QueryState loading={campaignQuery.isLoading} error={campaignQuery.error} what="campaign">
          {campaign !== undefined && (
            <Stack gap="lg">
              <Card title="Runs" subtitle="Select a run to inspect its node states.">
                <QueryState
                  loading={executionsQuery.isLoading}
                  error={executionsQuery.error}
                  what="run history"
                >
                  {executions.length === 0 ? (
                    <EmptyState
                      title="This campaign has never run"
                      description="Execute it to produce a run you can monitor node by node."
                    />
                  ) : (
                    <Stack gap="md">
                      <Field label="Run" htmlFor="execution-picker">
                        <Select
                          id="execution-picker"
                          value={activeExecutionId}
                          onChange={(e) => {
                            setChosenExecutionId(e.target.value);
                            setSelectedNodeId(null);
                          }}
                        >
                          {executions.map((execution) => (
                            <option key={execution.id} value={execution.id}>
                              {formatTime(execution.startedAt)} · {execution.state} · attempt{' '}
                              {execution.attempt}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      {activeExecution !== undefined && (
                        <Cluster gap="md">
                          <Badge tone={RUN_STATE_TONE[activeExecution.state] ?? 'neutral'} dot>
                            {activeExecution.state}
                          </Badge>
                          <span className="text-xs text-ink-muted">
                            attempt {activeExecution.attempt}
                          </span>
                          <span className="text-xs text-ink-muted">
                            started {formatTime(activeExecution.startedAt)}
                          </span>
                          <span className="text-xs text-ink-muted">
                            finished {formatTime(activeExecution.finishedAt)}
                          </span>
                        </Cluster>
                      )}
                    </Stack>
                  )}
                </QueryState>
              </Card>

              {openGates.length > 0 && (
                <Card title="Approval required">
                  <Stack gap="md">
                    {openGates.map((gate) => (
                      <Alert key={gate.nodeId} tone="warning" title={`Gate "${gate.nodeId}"`}>
                        <Stack gap="sm">
                          <p>
                            The run is paused here. Approving lets its dependents run. Rejecting
                            rolls back everything already completed in this run. Published work is
                            withdrawn, in reverse order.
                          </p>
                          {/* Show the reviewer exactly what approving will send out. */}
                          <GateReviewPreview
                            workspaceId={workspaceId}
                            assetIds={assetsGuardedByGate(gate.nodeId, dagNodes)}
                          />
                          <Cluster gap="sm">
                            <Button
                              size="sm"
                              loading={decideApproval.isPending}
                              onClick={() =>
                                decideApproval.mutate({
                                  executionId: activeExecutionId,
                                  nodeId: gate.nodeId,
                                  approved: true,
                                })
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              loading={decideApproval.isPending}
                              onClick={() =>
                                decideApproval.mutate({
                                  executionId: activeExecutionId,
                                  nodeId: gate.nodeId,
                                  approved: false,
                                })
                              }
                            >
                              Reject and roll back
                            </Button>
                          </Cluster>
                        </Stack>
                      </Alert>
                    ))}
                  </Stack>
                </Card>
              )}

              <Card title="Graph" subtitle="Select a node to see what it produced.">
                <QueryState
                  loading={detailQuery.isLoading && activeExecutionId !== ''}
                  error={detailQuery.error}
                  what="node states"
                >
                  <Stack gap="md">
                    <CampaignDAGViewer
                      nodes={viewNodes}
                      selected={selectedNodeId}
                      onSelect={(nodeId) => setSelectedNodeId(nodeId)}
                    />
                    {selectedNode !== undefined && (
                      <Card
                        title={selectedNode.nodeId}
                        subtitle={selectedNode.action}
                        actions={
                          selectedExecution !== undefined ? (
                            <Badge tone={NODE_STATE_TONE[selectedExecution.state] ?? 'neutral'} dot>
                              {selectedExecution.state.replace('_', ' ')}
                            </Badge>
                          ) : undefined
                        }
                      >
                        {selectedExecution === undefined ? (
                          <p className="text-sm text-ink-muted">
                            This node has no execution record in the selected run.
                          </p>
                        ) : (
                          <Stack gap="sm">
                            {selectedExecution.error !== null && (
                              <Alert tone="error" title="Error">
                                {selectedExecution.error}
                              </Alert>
                            )}
                            <p className="text-xs uppercase tracking-wide text-ink-muted">Output</p>
                            <pre className="overflow-x-auto rounded-md bg-surface-sunken p-3 font-mono text-xs text-ink">
                              {selectedExecution.output === null
                                ? 'No output recorded.'
                                : JSON.stringify(selectedExecution.output, null, 2)}
                            </pre>
                          </Stack>
                        )}
                      </Card>
                    )}
                  </Stack>
                </QueryState>
              </Card>

              <Grid min="lg">
                <Card title="Timeline">
                  {timelineEntries.length === 0 ? (
                    <p className="text-sm text-ink-muted">
                      No node has reported state in this run yet.
                    </p>
                  ) : (
                    <Timeline entries={timelineEntries} />
                  )}
                </Card>
                <Card title="Definition">
                  <Stack gap="sm">
                    <p className="text-sm text-ink-muted">
                      {campaign.dag.nodes.length} node
                      {campaign.dag.nodes.length === 1 ? '' : 's'} · created{' '}
                      {formatTime(campaign.createdAt)}
                    </p>
                    <Cluster gap="sm">
                      <Badge tone={RUN_STATE_TONE[campaign.state] ?? 'neutral'} dot>
                        {campaign.state}
                      </Badge>
                      {campaign.schedule !== null && (
                        <span className="text-xs text-ink-muted">
                          scheduled for {formatTime(campaign.schedule.targetStartTime)}
                        </span>
                      )}
                    </Cluster>
                  </Stack>
                </Card>
              </Grid>
            </Stack>
          )}
        </QueryState>
      </Stack>
      <Modal
        open={scheduleOpen}
        title="Schedule this campaign"
        onClose={() => setScheduleOpen(false)}
        footer={
          <>
            {campaign?.schedule != null && campaign.schedule.state === 'scheduled' && (
              <Button
                variant="ghost"
                loading={scheduleCampaign.isPending}
                onClick={() =>
                  scheduleCampaign.mutate(null, { onSuccess: () => setScheduleOpen(false) })
                }
              >
                Cancel schedule
              </Button>
            )}
            <Button variant="secondary" onClick={() => setScheduleOpen(false)}>
              Close
            </Button>
            <Button
              loading={scheduleCampaign.isPending}
              disabled={scheduleAt === ''}
              onClick={() =>
                scheduleCampaign.mutate(
                  { targetStartTime: new Date(scheduleAt).toISOString(), state: 'scheduled' },
                  { onSuccess: () => setScheduleOpen(false) },
                )
              }
            >
              Schedule run
            </Button>
          </>
        }
      >
        <Stack gap="md">
          <p className="text-sm text-ink-muted">
            The scheduler fires the campaign at this time (your local timezone), with catch-up if
            the server was briefly down. Approval gates still pause the run for a human.
          </p>
          <Field label="Run at" htmlFor="schedule-at">
            <Input
              id="schedule-at"
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
            />
          </Field>
          {campaign?.schedule != null && campaign.schedule.state === 'scheduled' && (
            <p className="text-xs text-ink-muted">
              Currently scheduled for {formatTime(campaign.schedule.targetStartTime)}.
            </p>
          )}
          {scheduleCampaign.error instanceof Error && (
            <Alert tone="error" title="Could not update the schedule">
              {scheduleCampaign.error.message}
            </Alert>
          )}
        </Stack>
      </Modal>
    </Page>
  );
}

/** Format a Date as the local value a datetime-local input expects (no timezone). */
function localInputValue(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
